import React, { useMemo, useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, PlayCircle, History, HelpCircle } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import { buildComparison, activatableIds } from './billedVsEnabledModel';

import { RubyGemMark } from '../RubyGemMark';
/**
 * Mirrors `@platform/shared-types`' `ActionBlockRun` / `ActionStepResult`
 * shape (see packages/shared-types/src/actionBlocks.ts). Kept local rather
 * than imported so this file's only cross-cutting dependency is the one
 * `apiService.runAction` method Task 5 is scoped to add — the run object
 * that comes back off the wire, and the raw audit-log rows this panel
 * filters client-side, are both handled as plain data here.
 */
export interface ActionStepResult {
  step: string;
  ok: boolean;
  detail?: string;
  error?: string;
  /** Set when the step succeeded but withheld part of the write — the shrinkage guard. */
  degraded?: string;
}

export interface ActionBlockRun {
  runId: string;
  blockId: string;
  intent: 'grant' | 'revoke' | 'none';
  source: 'manual' | 'policy' | 'onboarding';
  actor: string;
  reason?: string;
  status: 'ok' | 'partial' | 'failed';
  steps: ActionStepResult[];
  orderId?: string;
  at: number;
}

interface Props {
  tenantId: string;
  enabledPackages: string[];
  /** planPackageIds off the tenant's active PlanAssignment. `null` = not loaded yet, or failed to load. */
  billedPackages: string[] | null;
  billedLoading?: boolean;
  billedError?: string | null;
  packageNames: Record<string, string>;
  lastRuns: ActionBlockRun[];
  onRan: () => void | Promise<void>;
  /**
   * PRESENTATION ONLY — drops this panel's own card chrome.
   *
   * It is mounted two ways: standalone on TenantProfile's Packages tab, where
   * the border and shadow are what separate it from the tab, and inside
   * `PackageActivationQuickView`, which is ALREADY a white rounded card. There
   * it drew a second frame a few pixels inside the first — the doubled edge
   * visible in the modal, and the reason its header and the modal's competed
   * instead of reading as one thing.
   */
  embedded?: boolean;
}

const BLOCK_LABELS: Record<string, string> = {
  activate_packages: 'Activate packages',
  activate_all: 'Activate all access',
  suspend_features: 'Suspend features',
  suspend_all: 'Suspend all access',
  collections_only: 'Collections only',
};

const STATUS_STYLES: Record<string, string> = {
  ok: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  partial: 'bg-amber-100 text-amber-700 border-amber-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
};

const formatDateTime = (ts?: number | null): string => {
  if (!ts) return 'N/A';
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/** The one ruby the super-admin theme uses for a primary action. */
const RUBY = '#DC143C';

/**
 * ONE STATE CELL — "Billed" / "Enabled", or the absence of either.
 *
 * A MATCH IS QUIET AND A MISMATCH IS LOUD. Every cell used to be the same
 * weight, so fourteen matching packages rendered twenty-eight identically
 * sized green ticks and the one red row had to compete with all of them. A
 * match is now a light tick with muted text; only a miss gets a filled pill.
 * The words stay in both cases — the column heading alone does not tell a
 * screen reader which way the row went.
 */
const StateCell: React.FC<{ on: boolean; onLabel: string; offLabel: string }> = ({ on, onLabel, offLabel }) => (
  on ? (
    <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-emerald-700">
      <CheckCircle2 size={14} className="shrink-0 text-emerald-500" />
      {onLabel}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11.5px] font-bold text-red-700">
      <XCircle size={13} className="shrink-0" />
      {offLabel}
    </span>
  )
);

const BilledVsEnabledPanel: React.FC<Props> = ({
  tenantId, enabledPackages, billedPackages, billedLoading, billedError,
  packageNames, lastRuns, onRan, embedded = false,
}) => {
  const [running, setRunning] = useState(false);

  const billedKnown = billedPackages !== null;
  // A package the catalog cannot name is only DELETED once the catalog has
  // actually arrived; until then it is merely unnamed.
  const catalogLoaded = Object.keys(packageNames).length > 0;

  const rows = useMemo(
    () => buildComparison({ enabledPackages, billedPackages, packageNames, catalogLoaded }),
    [enabledPackages, billedPackages, packageNames, catalogLoaded],
  );

  const missingFromEnabled = useMemo(
    () => activatableIds({ enabledPackages, billedPackages }),
    [enabledPackages, billedPackages],
  );

  const unknownRows = useMemo(() => rows.filter((r) => r.unknown), [rows]);

  let runDisabledReason: string | null = null;
  if (billedLoading) {
    runDisabledReason = 'Loading the active plan assignment…';
  } else if (!billedKnown) {
    runDisabledReason = billedError || 'Could not load the active plan assignment — activation is unsafe to offer blind.';
  } else if ((billedPackages || []).length === 0) {
    // The safety rule: an empty billed set must never be presented as a fix.
    // `activate_packages` reads packages off the active PlanAssignment — with
    // none, it would run and change nothing.
    runDisabledReason = 'No active plan assignment to activate from.';
  } else if (missingFromEnabled.length === 0) {
    // "Nothing to activate" and "nothing is wrong" are different facts. The
    // button can only ever ADD, so an enabled-but-not-billed row survives it —
    // and saying "already match" while such a row sits in red above is a
    // contradiction an operator has to resolve for themselves.
    runDisabledReason = rows.some((r) => r.mismatch)
      ? 'Nothing for activation to add — the differences left are enabled but not billed, which this cannot fix.'
      : 'Billed and enabled already match — nothing to activate.';
  }

  const canRun = !runDisabledReason;

  const handleRun = async () => {
    if (!canRun || running) return;
    setRunning(true);
    try {
      const run = await api.billingCatalog.runAction(tenantId, 'activate_packages');
      if (run.status === 'ok') {
        toastService.success('Activation ran — billed packages are now enabled.');
      } else if (run.status === 'partial') {
        toastService.warning('Activation ran but only partially completed — see the run below.');
      } else {
        toastService.error('Activation failed — see the run below.');
      }
      await onRan();
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to run activation');
    } finally {
      setRunning(false);
    }
  };

  const recentRuns = (lastRuns || []).slice(0, 5);

  /**
   * HOW MANY ROWS DISAGREE — stated once, at the top.
   *
   * Derived from `rows`, which is already on screen; it reads no new data and
   * changes nothing. It exists because the answer to "is this tenant right?"
   * was previously only obtainable by scanning fourteen rows of ticks.
   */
  const mismatchCount = rows.filter((r) => r.mismatch).length;

  return (
    <div className={embedded
      ? ''
      : 'overflow-hidden rounded-2xl border border-stone-200/60 bg-white shadow-sm'}>
      {/* THE ACTION SITS UNDER THE TITLE, NOT BESIDE IT. Side by side, the
          header wrapped at modal width and left the button stranded in the
          middle of the row with its explanation centred under it. */}
      <div className={`border-b border-stone-100 ${embedded ? 'px-5 pb-4 pt-1' : 'px-5 py-4'}`}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h3 className="text-[15px] font-bold tracking-tight text-stone-900">Billed vs Enabled</h3>
          {rows.length > 0 && (
            <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${
              mismatchCount > 0
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}>
              {mismatchCount > 0
                ? `${mismatchCount} ${mismatchCount === 1 ? 'difference' : 'differences'}`
                : `All ${rows.length} match`}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[12.5px] leading-relaxed text-stone-500">
          What Finance billed against what the software actually turned on.
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <button
            onClick={handleRun}
            disabled={!canRun || running}
            title={runDisabledReason || undefined}
            style={canRun && !running ? { background: RUBY } : undefined}
            /* A DISABLED PRIMARY IS GREY, NOT FADED RUBY. `opacity-40` on a
               filled button produced a washed-out pink slab that read as a
               rendering fault rather than a control that is off. */
            className="inline-flex min-h-[40px] items-center gap-2 rounded-xl px-4 text-[13px] font-bold text-white transition-colors hover:brightness-110 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-500 disabled:hover:brightness-100"
          >
            {running ? <RubyGemMark size={15} drawing color="currentColor" /> : <PlayCircle size={15} />}
            Run activation
          </button>
          {runDisabledReason && (
            <p className="min-w-0 flex-1 text-[12px] leading-relaxed text-stone-500">{runDisabledReason}</p>
          )}
        </div>
      </div>

      {/* Billed vs Enabled table */}
      <div className="px-5 py-4">
        {rows.length === 0 ? (
          <p className="text-sm text-stone-400">No billed or enabled packages to compare.</p>
        ) : (
          <div className="-mx-2 overflow-x-auto">
            <table className="w-full min-w-[440px] border-separate border-spacing-0">
              <thead>
                <tr className="text-left text-[10px] font-extrabold uppercase tracking-[.12em] text-stone-400">
                  <th className="px-2 pb-2">Package</th>
                  <th className="w-[168px] px-2 pb-2">Billed</th>
                  <th className="w-[168px] px-2 pb-2">Enabled</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ id, label, unknown, inBilled, inEnabled, mismatch }) => (
                  /* A ROW THAT DISAGREES IS TINTED, not merely red-lettered —
                     at fourteen rows the colour of one word was not enough to
                     find. `group` so the whole row lifts on hover. */
                  <tr key={id} className={`group ${mismatch ? 'bg-red-50/60' : 'hover:bg-stone-50'}`}>
                    <td className={`rounded-l-lg border-b border-stone-100 px-2 py-2.5 align-middle text-[13px] font-semibold ${
                      mismatch ? 'text-red-800' : 'text-stone-800'
                    }`}>
                      <span className={unknown ? 'font-mono text-[11.5px]' : undefined}>{label}</span>
                      {/* A DELETED PACKAGE IS A DIFFERENT FINDING from an
                          unbilled one, and the two used to render identically
                          — a bare id in red, leaving the operator to work out
                          which they were looking at. `enabledPackages` only
                          ever grows, so no activation can clear this row. */}
                      {unknown && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 align-middle text-[11px] font-semibold text-amber-700"
                          title="This id is not in the package catalog — the package was deleted while this tenant still held it. It grants no features, and activation cannot clear it."
                        >
                          <HelpCircle size={11} /> no longer in the catalog
                        </span>
                      )}
                    </td>
                    <td className="border-b border-stone-100 px-2 py-2.5 align-middle">
                      {!billedKnown
                        ? <span className="text-[12px] text-stone-400">unavailable</span>
                        : <StateCell on={inBilled} onLabel="Billed" offLabel="Not billed" />}
                    </td>
                    <td className="rounded-r-lg border-b border-stone-100 px-2 py-2.5 align-middle">
                      <StateCell on={inEnabled} onLabel="Enabled" offLabel="Not enabled" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {unknownRows.length > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[12px] leading-relaxed text-amber-800">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
            <span>
              {unknownRows.length === 1 ? 'One package id is' : `${unknownRows.length} package ids are`}
              {' '}no longer in the catalog. The package was deleted while this tenant still held it —
              it grants no features, and because activation can only ever add, this row cannot be
              cleared from here.
            </span>
          </div>
        )}
        {!billedKnown && !billedLoading && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[12px] leading-relaxed text-amber-800">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
            <span>{billedError || 'The active plan assignment could not be loaded, so the Billed column is unavailable.'}</span>
          </div>
        )}
      </div>

      {/* Last runs */}
      <div className="border-t border-stone-100 px-5 py-4">
        <div className="mb-3 flex items-center gap-2">
          <History size={14} className="text-stone-400" />
          <h4 className="text-[10px] font-extrabold uppercase tracking-[.12em] text-stone-400">Last 5 runs</h4>
        </div>
        {recentRuns.length === 0 ? (
          <p className="rounded-lg border border-dashed border-stone-200 px-3 py-4 text-center text-[12.5px] text-stone-400">
            No action-block runs recorded for this tenant yet.
          </p>
        ) : (
          <div className="space-y-2.5">
            {recentRuns.map((run) => (
              <div key={run.runId} className="rounded-xl border border-stone-200 bg-stone-50/60 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${STATUS_STYLES[run.status] || 'bg-stone-100 text-stone-600 border-stone-200'}`}>
                      {run.status}
                    </span>
                    <span className="text-[13px] font-bold text-stone-800">
                      {BLOCK_LABELS[run.blockId] || run.blockId}
                    </span>
                    <span className="text-[11.5px] text-stone-400">via {run.source}</span>
                  </div>
                  <span className="text-[11.5px] tabular-nums text-stone-400">{formatDateTime(run.at)}</span>
                </div>
                {run.steps?.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {run.steps.map((step, i) => (
                      <li key={`${run.runId}-${step.step}-${i}`} className="text-[12px]">
                        <div className="flex items-start gap-1.5">
                          {step.ok
                            ? <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-emerald-500" />
                            : <XCircle size={13} className="mt-0.5 shrink-0 text-red-600" />}
                          <div className="min-w-0 leading-relaxed">
                            <span className="font-semibold text-stone-700">{step.step}</span>
                            {step.detail && <span className="text-stone-500"> — {step.detail}</span>}
                            {step.error && <span className="font-medium text-red-600"> — {step.error}</span>}
                          </div>
                        </div>
                        {/* The shrinkage guard's message — an operator must never miss this,
                            so it renders in full, never truncated. */}
                        {step.degraded && (
                          <div className="ml-[19px] mt-1 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-800">
                            <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-600" />
                            <span className="whitespace-pre-wrap break-words leading-relaxed">{step.degraded}</span>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BilledVsEnabledPanel;
